<template>
  <div class="hr-co-input" :class="{ helper, invalid }">
    <div class="hr-co-input__label-container">
      <div v-if="label" class="hr-co-input__label">
        {{ label }}
        <small class="hr-co-input__label__optional">
          {{ optional ? '(optional)' : '' }}
        </small>
      </div>
      <p v-html="helper" class="hr-co-input__helper"></p>
    </div>
    <div class="hr-co-input__input">
      <div
        class="hr-co-input__input-group"
        :class="{ prepend: $slots.prepend, append: $slots.append }"
      >
        <div class="hr-co-input__input-container">
          <co-input
            ref="inputRef"
            :model-value="state.theValue"
            :disabled="disabled"
            :type="inputConfig"
            :placeholder="placeholder"
            :digit-group-size="digitGroupSize"
            :class="{
              'fs-mask': masked,
              'input-sm': small,
              'input-lg': large,
              icon:
                $slots.icon ||
                state.isLoading ||
                state.isInvalid === true ||
                state.isInvalid === false,
              password: state.isPassword,
            }"
            @input="input"
            @click="$emit('click')"
            @blur="blur"
            @focus="focus"
            @keyup.enter="enter"
            @keyup.exact="keyUp"
            event-validation
            :valid="state.isInvalid === false && !state.isLoading"
          >
            <template v-if="$slots.prepend" v-slot:prepend>
              <slot name="prepend"></slot>
            </template>
            <template v-if="$slots.append" v-slot:append>
              <slot name="append"></slot>
            </template>
          </co-input>
        </div>
      </div>
    </div>

    <div
      v-if="$slots.invalid && state.isInvalid === true"
      class="hr-co-input__invalid"
    >
      <slot name="invalid"></slot>
    </div>
  </div>
</template>

<script>
import { browser, isMobile } from '@/core/browser-detection';
import { reactive, computed, watch, nextTick, ref } from 'vue';
import { CoInput } from '@deputy/copilot2';
import { formatAlphaNumeric } from '@/lib/alpha-numeric';
import { logWebHrError } from '@/core/logger/log-wrapper';

const TYPES = {
  TEXT: 'text',
  PASSWORD: 'password',
  NUMBER: 'number',
  PHONE: 'tel',
  BSB: 'bsb',
  SORTCODE: 'sortCode',
  MOBILE: 'phone',
  ALPHA_NUMERIC: 'alphaNumeric',
  TFN_AU: 'tfnAu',
};

export default {
  name: 'hr-co-input',
  components: { CoInput },
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (val) => Object.values(TYPES).includes(val),
    },
    formatter: {
      type: Function,
      default: null,
    },
    validator: {
      type: Function,
      default: null,
    },
    unformatter: {
      type: Function,
      default: null,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    small: {
      type: Boolean,
      default: false,
    },
    label: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: [String, Number],
      default: null,
    },
    value: {
      type: [String, Number, Boolean],
      default: null,
    },
    invalid: {
      type: Boolean,
      default: null,
    },
    autoValidation: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
    digitGroupSize: {
      type: Number,
      default: 0,
    },
    helper: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    masked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      theType: props.type,
      isPassword: props.type === TYPES.PASSWORD,
      theValue: props.value ? props.value : '',
      isInvalid: computed(() => props.invalid),
      isLoading: computed(() => props.loading),
      unformatted: null,
      validatorResult: null,
      browser: browser(),
    });

    const inputRef = ref(null);
    const inputConfig = computed(() => {
      if (isMobile()) {
        if (state.theType === TYPES.NUMBER) {
          return TYPES.NUMBER;
        }
        if (state.theType === TYPES.BSB) {
          return TYPES.MOBILE;
        }
        if (state.theType === TYPES.SORTCODE) {
          return TYPES.MOBILE;
        }

        return state.theType;
      }

      return state.theType === TYPES.PASSWORD
        ? TYPES.PASSWORD
        : state.theType === TYPES.NUMBER
        ? TYPES.NUMBER
        : TYPES.TEXT;
    });

    watch(
      () => props.value,
      (newVal) => {
        if (props.formatter) {
          state.theValue = props.formatter(newVal);
          return true;
        }

        state.theValue = newVal;
      }
    );

    function initialize() {
      if (props.value) {
        if (props.formatter) {
          state.theValue = props.formatter(props.value);
          return true;
        }

        state.theValue = props.value;
      }
    }
    initialize();

    async function test() {
      if (props.formatter) {
        state.theValue = props.formatter(state.theValue);
      }

      if (props.max) {
        if (state.theValue.length > props.max) {
          state.theValue = state.theValue.slice(0, props.max);
          return false;
        }
      }

      state.unformatted = state.unformatter
        ? props.unformatter(state.theValue)
        : state.theValue;

      if (!state.theValue || !props.autoValidation) {
        return true;
      }

      state.validatorResult = null;
    }

    function emitEvent(evt, realEvt) {
      emit(
        evt,
        state.theValue,
        {
          value: state.theValue,
          unformatted: state.unformatted,
          isInvalid: state.isInvalid,
          helper: state.validatorResult,
        },
        realEvt
      );
    }

    async function input(evt) {
      state.theValue = evt;
      if (state.theType === TYPES.ALPHA_NUMERIC) {
        state.theValue = formatAlphaNumeric(evt);
      }
      try {
        await test(evt);
        emitEvent('input', evt);
      } catch (e) {
        logWebHrError({
          message: 'hr-co-input input ERROR',
          source: 'hr-co-input:input',
          metadata: {
            eventType: state.theType,
            hasValue: !!state.theValue,
          },
          error: e,
        });
      }
    }

    async function blur(evt) {
      try {
        await test(evt);
        emitEvent('blur', evt);
      } catch (e) {
        logWebHrError({
          message: 'hr-co-input blur ERROR',
          source: 'hr-co-input:blur',
          metadata: {
            eventType: state.theType,
            hasValue: !!state.theValue,
          },
          error: e,
        });
      }
    }

    function enter() {
      emit('enter', state.theValue);
    }
    function passwordToggle() {
      state.theType =
        state.theType === TYPES.PASSWORD ? TYPES.TEXT : TYPES.PASSWORD;
    }

    function focus() {
      nextTick(() => inputRef.value?.focus());
      emit('focus');
    }
    function keyUp(evt) {
      emit('keyup', evt, state.theValue);
    }

    return {
      state,
      inputRef,
      inputConfig,
      input,
      blur,
      enter,
      focus,
      keyUp,
      passwordToggle,
    };
  },
};
</script>

<style scoped>
input {
  width: 100%;
}

.hr-co-input__label {
  font-weight: 600;
  color: var(--co-text-primary);
  margin-bottom: 4px;
}

::v-deep(svg.co-input__valid-icon) {
  margin-bottom: 2px !important;
  height: 16px !important;
  width: 16px !important;
}

.hr-co-input__input {
  position: relative;
}

.hr-co-input {
  width: 100%;
  position: relative;
}

.hr-co-input__password-toggle {
  cursor: pointer;
  text-transform: uppercase;
  display: inline;
  padding-right: 5px;
  font-size: 20px;
}

.hr-co-input__password-toggle .DSIcon {
  font-size: 1.8em;
}

.hr-co-input__icon {
  display: block;
  float: left;
  padding-right: 5px;
  font-size: 20px;
  height: 100%;
}

.hr-co-input__invalid {
  color: var(--co-text-attention);
  margin-top: 5px;
}

.hr-co-input__loading-icon,
.hr-co-input__invalid-icon,
.hr-co-input__valid-icon {
  display: none;
}

.hr-co-input__loading-icon.active,
.hr-co-input__invalid-icon.active,
.hr-co-input__valid-icon.active {
  display: block;
}

.hr-co-input__input-container {
  position: relative;
  flex: 1;
}

.hr-co-input__input__states {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
}

.hr-co-input__helper {
  font-size: 14px;
  color: var(--co-text-secondary);
}

.hr-co-input__label__optional {
  font-weight: 100;
}

.hr-co-input__input-group.prepend {
  position: relative;
  display: flex;
  align-items: flex-end;
}

.hr-co-input__input-group.append {
  position: relative;
  display: flex;
  align-items: flex-end;
}

.hr-co-input__input-group.append .hr-co-input__input__input,
.hr-co-input__input-group.prepend .hr-co-input__input__input {
  flex-grow: 1;
  flex: 20;
}

.hr-co-input__input__input.icon,
.hr-co-input__input__input.password {
  padding-right: 55px;
}

.hr-co-input__input__input.password.icon {
  padding-right: 90px;
}

@media only screen and (max-width: 768px) {
  .hr-co-input,
  .hr-co-input__helper,
  :deep(.co-input__inner),
  :deep(.co-input__inner:focus),
  .co-input--default {
    font-size: 16px;
  }
}
</style>
